import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { message, Modal, Pagination, Table } from "antd";
import { useRecoilValue } from "recoil";
import { HeaderTable, PermissionDirect } from "../../Components";
import { UserAtom } from "../../store";
import { checkPermissions } from "../../utils/checkPermissions";
import {
  deleteDataLock,
  getAllDataLocks,
  getDataLock,
} from "../../services/domains/data-lock";
import { buildDataLockColumns } from "./columns";
import { DataLockDetailModal } from "./DataLockDetailModal";
import { DataLockFormModal } from "./DataLockFormModal";
import type { DataLockRecord } from "./types";
import styles from "./DataLock.module.scss";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
type DataLockFormMode = "create" | "edit";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string" &&
    error.message
  ) {
    return error.message;
  }

  return fallback;
};

export const DataLock = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const permissionState = useRecoilValue<any>(UserAtom.permissions);
  const features = permissionState?.features;
  const hasPermissionData = Boolean(features);
  const canViewDataLock = checkPermissions("LOCK_DATA", "view", features);
  const canAddDataLock =
    canViewDataLock && checkPermissions("LOCK_DATA", "add", features);
  const canEditDataLock =
    canViewDataLock && checkPermissions("LOCK_DATA", "edit", features);
  const canDeleteDataLock =
    canViewDataLock && checkPermissions("LOCK_DATA", "delete", features);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DataLockRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<DataLockRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<DataLockFormMode | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [listReloadVersion, setListReloadVersion] = useState(0);
  const detailRequestVersionRef = useRef(0);
  const deleteConfirmRef = useRef<ReturnType<typeof Modal.confirm> | null>(
    null,
  );
  const deleteRequestRef = useRef<{
    id: string;
    promise: Promise<void>;
  } | null>(null);
  const mountedRef = useRef(true);
  const selectedIdRef = useRef(selectedId);
  const formStateRef = useRef({ mode: formMode, id: editingId });
  const paginationRef = useRef(pagination);
  const dataSourceRef = useRef(dataSource);
  const permissionActionsRef = useRef({
    canView: canViewDataLock,
    canAdd: canAddDataLock,
    canEdit: canEditDataLock,
    canDelete: canDeleteDataLock,
  });

  selectedIdRef.current = selectedId;
  formStateRef.current = { mode: formMode, id: editingId };
  paginationRef.current = pagination;
  dataSourceRef.current = dataSource;
  permissionActionsRef.current = {
    canView: canViewDataLock,
    canAdd: canAddDataLock,
    canEdit: canEditDataLock,
    canDelete: canDeleteDataLock,
  };

  useEffect(() => {
    if (!hasPermissionData || !canViewDataLock) {
      setLoading(false);
      setDataSource([]);
      setPagination((previous) => ({
        ...previous,
        total: 0,
      }));
      return;
    }

    let active = true;
    setLoading(true);
    setDataSource([]);

    getAllDataLocks({
      current: pagination.current,
      pageSize: pagination.pageSize,
    })
      .then((response) => {
        if (!active) return;

        setDataSource(Array.isArray(response?.data) ? response.data : []);
        setPagination((previous) => ({
          ...previous,
          total:
            typeof response?.totalItem === "number"
              ? response.totalItem
              : 0,
        }));
      })
      .catch((error: unknown) => {
        if (!active) return;

        setPagination((previous) => ({
          ...previous,
          total: 0,
        }));
        messageApi.error(
          getErrorMessage(
            error,
            "Không thể tải danh sách khóa dữ liệu",
          ),
        );
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    canViewDataLock,
    hasPermissionData,
    listReloadVersion,
    messageApi,
    pagination.current,
    pagination.pageSize,
  ]);

  const destroyDeleteConfirm = useCallback(() => {
    const current = deleteConfirmRef.current;
    deleteConfirmRef.current = null;
    current?.destroy();
  }, []);

  const clearDeleteConfirm = useCallback(
    (handle: ReturnType<typeof Modal.confirm>) => {
      if (deleteConfirmRef.current === handle) {
        deleteConfirmRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      detailRequestVersionRef.current += 1;
      destroyDeleteConfirm();
    };
  }, [destroyDeleteConfirm]);

  const loadDetail = useCallback(async (id: string) => {
    if (!permissionActionsRef.current.canView) return;

    const requestVersion = detailRequestVersionRef.current + 1;
    detailRequestVersionRef.current = requestVersion;
    setDetailData(null);
    setDetailError(null);
    setDetailLoading(true);

    try {
      const dataLock = await getDataLock(id);
      if (
        detailRequestVersionRef.current !== requestVersion ||
        !permissionActionsRef.current.canView
      ) {
        return;
      }

      setDetailData(dataLock);
    } catch (error: unknown) {
      if (
        detailRequestVersionRef.current !== requestVersion ||
        !permissionActionsRef.current.canView
      ) {
        return;
      }

      setDetailError(
        getErrorMessage(error, "Không thể tải chi tiết khóa dữ liệu"),
      );
    } finally {
      if (detailRequestVersionRef.current === requestVersion) {
        setDetailLoading(false);
      }
    }
  }, []);

  const handleView = useCallback(
    (id: string) => {
      if (!permissionActionsRef.current.canView) return;

      setSelectedId(id);
      void loadDetail(id);
    },
    [loadDetail],
  );

  const handleRetry = useCallback(() => {
    if (!permissionActionsRef.current.canView) return;

    if (selectedId) {
      void loadDetail(selectedId);
    }
  }, [loadDetail, selectedId]);

  const handleCloseDetail = useCallback(() => {
    detailRequestVersionRef.current += 1;
    selectedIdRef.current = null;
    setSelectedId(null);
    setDetailData(null);
    setDetailLoading(false);
    setDetailError(null);
  }, []);

  const handleOpenCreate = useCallback(() => {
    if (
      !permissionActionsRef.current.canView ||
      !permissionActionsRef.current.canAdd
    ) {
      return;
    }

    formStateRef.current = { mode: "create", id: null };
    setFormMode("create");
    setEditingId(null);
  }, []);

  const handleEdit = useCallback((id: string) => {
    if (
      !permissionActionsRef.current.canView ||
      !permissionActionsRef.current.canEdit
    ) {
      return;
    }

    formStateRef.current = { mode: "edit", id };
    setFormMode("edit");
    setEditingId(id);
  }, []);

  const handleCloseForm = useCallback(() => {
    formStateRef.current = { mode: null, id: null };
    setFormMode(null);
    setEditingId(null);
  }, []);

  useEffect(() => {
    if (canViewDataLock) return;

    handleCloseDetail();
    handleCloseForm();
    destroyDeleteConfirm();
  }, [
    canViewDataLock,
    destroyDeleteConfirm,
    handleCloseDetail,
    handleCloseForm,
  ]);

  const reloadCurrentPage = useCallback(() => {
    setListReloadVersion((previous) => previous + 1);
  }, []);

  const handleFormSaved = useCallback(() => {
    const savedMode = formStateRef.current.mode;
    handleCloseForm();

    if (savedMode === "create" && paginationRef.current.current !== 1) {
      setPagination((previous) => ({ ...previous, current: 1 }));
      return;
    }

    reloadCurrentPage();
  }, [handleCloseForm, reloadCurrentPage]);

  const executeDelete = useCallback(
    (id: string): Promise<void> => {
      if (
        !permissionActionsRef.current.canView ||
        !permissionActionsRef.current.canDelete
      ) {
        return Promise.resolve();
      }

      const activeRequest = deleteRequestRef.current;
      if (activeRequest) {
        return activeRequest.promise;
      }

      setDeletingId(id);
      let request!: Promise<void>;
      request = (async () => {
        try {
          await deleteDataLock(id);
          if (!mountedRef.current) return;

          messageApi.success("Xóa khóa dữ liệu thành công");
          if (selectedIdRef.current === id) {
            handleCloseDetail();
          }
          if (
            formStateRef.current.mode === "edit" &&
            formStateRef.current.id === id
          ) {
            handleCloseForm();
          }

          const isLastDisplayedRow = dataSourceRef.current.length === 1;
          if (
            isLastDisplayedRow &&
            paginationRef.current.current > 1
          ) {
            setPagination((previous) => ({
              ...previous,
              current: previous.current - 1,
            }));
          } else {
            reloadCurrentPage();
          }
        } catch (error: unknown) {
          if (!mountedRef.current) return;

          messageApi.error(
            getErrorMessage(error, "Không thể xóa khóa dữ liệu"),
          );
        } finally {
          if (deleteRequestRef.current?.promise === request) {
            deleteRequestRef.current = null;
            if (mountedRef.current) {
              setDeletingId(null);
            }
          }
        }
      })();
      deleteRequestRef.current = { id, promise: request };
      return request;
    },
    [
      handleCloseDetail,
      handleCloseForm,
      messageApi,
      reloadCurrentPage,
    ],
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (
        !permissionActionsRef.current.canView ||
        !permissionActionsRef.current.canDelete
      ) {
        return;
      }
      if (deleteRequestRef.current) return;

      destroyDeleteConfirm();
      const recordName = dataSourceRef.current.find(
        (record) => record._id === id,
      )?.name;
      const target = recordName
        ? `khóa dữ liệu "${recordName}"`
        : "khóa dữ liệu này";

      let confirmHandle: ReturnType<typeof Modal.confirm>;
      confirmHandle = Modal.confirm({
        centered: true,
        title: "Xóa khóa dữ liệu",
        content: `Bạn có chắc chắn muốn xóa ${target}? Hành động này không thể hoàn tác.`,
        okText: "Xóa",
        cancelText: "Hủy",
        okButtonProps: { danger: true },
        onOk: () => {
          const request = executeDelete(id);
          const clearSettledConfirm = () =>
            clearDeleteConfirm(confirmHandle);
          void request.then(clearSettledConfirm, clearSettledConfirm);
          return request;
        },
        onCancel: () => clearDeleteConfirm(confirmHandle),
      });
      deleteConfirmRef.current = confirmHandle;
    },
    [
      clearDeleteConfirm,
      destroyDeleteConfirm,
      executeDelete,
    ],
  );

  const columns = useMemo(
    () =>
      buildDataLockColumns({
        current: pagination.current,
        pageSize: pagination.pageSize,
        onView: handleView,
        onEdit: handleEdit,
        onDelete: handleDelete,
        deletingId,
        canEdit: canViewDataLock && canEditDataLock,
        canDelete: canViewDataLock && canDeleteDataLock,
      }),
    [
      canDeleteDataLock,
      canEditDataLock,
      canViewDataLock,
      deletingId,
      handleDelete,
      handleEdit,
      handleView,
      pagination.current,
      pagination.pageSize,
    ],
  );
  const canSubmitDataLockForm =
    canViewDataLock &&
    (formMode === "edit" ? canEditDataLock : canAddDataLock);

  return (
    <PermissionDirect screen={["LOCK_DATA"]}>
      <div className={styles.DataLock}>
      {contextHolder}
      <div className="container">
        <HeaderTable
          title="Khóa dữ liệu"
          buttons={[
            {
              type: "primary",
              icon: <PlusOutlined />,
              children: "Thêm mới",
              disabled: !canViewDataLock || !canAddDataLock,
              onClick: handleOpenCreate,
            },
          ]}
        />
        <div className={styles.TableWrapper}>
          <Table<DataLockRecord>
            bordered
            size="small"
            loading={loading}
            rowKey="_id"
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            scroll={{ x: "max-content" }}
          />
        </div>
        <Pagination
          className={styles.Pagination}
          align="end"
          showSizeChanger
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          total={pagination.total}
          current={pagination.current}
          pageSize={pagination.pageSize}
          onChange={(current, pageSize) =>
            setPagination((previous) => ({
              ...previous,
              current,
              pageSize,
            }))
          }
        />
      </div>
      <DataLockFormModal
        open={formMode !== null}
        mode={formMode ?? "create"}
        dataLockId={formMode === "edit" ? editingId : null}
        canSubmit={canSubmitDataLockForm}
        onClose={handleCloseForm}
        onSaved={handleFormSaved}
      />
      <DataLockDetailModal
        open={selectedId !== null}
        loading={detailLoading}
        error={detailError}
        dataLock={detailData}
        onClose={handleCloseDetail}
        onRetry={handleRetry}
      />
      </div>
    </PermissionDirect>
  );
};
